import Section from '../components/Section';

/**
 * Vpnee Privacy Policy. Written to satisfy Google Play's User Data policy
 * and Data Safety declarations, plus GDPR / CCPA / Sri Lanka DPA baselines.
 * Every "data type" bullet here maps 1:1 to a Play Console Data Safety row.
 * Changing what the app collects means updating BOTH this document and the
 * Play Console declaration — they must never disagree.
 */
const EFFECTIVE = 'August 24, 2026';

export default function Privacy() {
  return (
    <Section chip="Legal" title="Privacy Policy" subtitle={`Effective ${EFFECTIVE}. What we collect, why, how long, and how to make us delete it.`}>
      <article className="glass p-6 sm:p-10 md:p-12 legal max-w-3xl">
        <p><strong>Summary in one paragraph.</strong> Vpnee is a zero-log VPN. We store the minimum data needed to run your account and enforce plan limits — nothing about what you do online. We never sell your personal information. You can request a copy or deletion of your data at any time by emailing <a href="mailto:privacy@tnidu.com">privacy@tnidu.com</a> or using the in-app <em>Delete account</em> flow.</p>

        <h2>1. Who we are</h2>
        <p>The Vpnee app and website are operated by <strong>TNIDU Inc.</strong> ("we", "us", "our"), registered in Colombo, Sri Lanka. TNIDU Inc. is the data controller for the personal information described in this policy.</p>
        <p>Contact: <a href="mailto:privacy@tnidu.com">privacy@tnidu.com</a> · Postal: TNIDU Inc., Colombo 00500, Sri Lanka.</p>

        <h2>2. What this policy covers</h2>
        <p>This policy covers the Vpnee mobile app (Android and, when released, iOS), the Vpnee desktop apps (Windows, Linux), and the website at <a href="https://vpnee.tnidu.com">vpnee.tnidu.com</a>. It does not cover third-party services you reach through Vpnee — those have their own policies.</p>

        <h2>3. Data we collect</h2>
        <p>Only the categories listed below. If a data type is not on this list, we do not collect it.</p>

        <h3>3.1 Account data</h3>
        <ul>
          <li><strong>Username</strong> — chosen by you at sign-up.</li>
          <li><strong>Email address</strong> — optional. Used only for password recovery, payment receipts, and important service notices.</li>
          <li><strong>Password hash</strong> — one-way hashed (bcrypt). We never store or transmit your plaintext password.</li>
          <li><strong>VLESS UUID</strong> — generated server-side, needed to route your VPN traffic. Not linked to what you do online.</li>
          <li><strong>Third-party sign-in identifier</strong> — if you use Google or Facebook sign-in, we store the provider's email (never the token) to match your account.</li>
        </ul>

        <h3>3.2 App activity & diagnostics</h3>
        <ul>
          <li><strong>Cumulative bytes used</strong> — a single running counter per account, so plan limits can be enforced. Not per-session, not per-destination.</li>
          <li><strong>Crash logs</strong> — de-identified stack traces sent when the app crashes, retained 30 days.</li>
          <li><strong>Non-fatal error diagnostics</strong> — the app version, OS version, device model, and the error code. No personal data in the payload.</li>
        </ul>

        <h3>3.3 Device information (login only)</h3>
        <ul>
          <li><strong>Device fingerprint</strong> — brand, model, OS version, app version, locale, timezone, and network type. Captured only at login for account-security anomaly detection.</li>
          <li><strong>Approximate location</strong> — the country / region inferred from the IP address you signed in from, stored coarsely (country code only) to detect account takeover.</li>
        </ul>

        <h3>3.4 Payment data</h3>
        <ul>
          <li>Handled entirely by <strong>Stripe</strong> (card) or <strong>Alipay</strong>. Vpnee never sees your card number, CVV, or full billing address. We store only the payment reference ID, the amount, the currency, and the invoice date — enough to power your receipts and refund eligibility.</li>
        </ul>

        <h3>3.5 Support data</h3>
        <ul>
          <li>When you contact us or use in-app support, we store the messages you send us so we can reply and follow up.</li>
        </ul>

        <h3>3.6 Data we explicitly DO NOT collect</h3>
        <ul>
          <li>Your browsing history or destination IP addresses.</li>
          <li>DNS queries.</li>
          <li>Per-session or per-destination timestamps.</li>
          <li>Packet contents.</li>
          <li>Precise device location (GPS).</li>
          <li>Contacts, calendar, SMS, photos, microphone, camera, health, or fitness data.</li>
          <li>Advertising identifiers. Vpnee ships no advertising SDKs.</li>
        </ul>

        <h2>4. How we use your data</h2>
        <ul>
          <li><strong>Provide the VPN service</strong> — authenticate your account, route your traffic, enforce plan limits.</li>
          <li><strong>Billing</strong> — process payments, issue receipts, honour refunds.</li>
          <li><strong>Security & fraud prevention</strong> — detect anomalies (impossible-travel logins, credential stuffing), block abuse, protect the network.</li>
          <li><strong>Product improvement</strong> — aggregate, de-identified analytics on crashes and errors so we can fix bugs.</li>
          <li><strong>Customer support</strong> — respond to your messages.</li>
          <li><strong>Legal compliance</strong> — respond to lawful requests where we are legally required to.</li>
        </ul>
        <p>We do not use your data for advertising, profiling, or automated decision-making with legal effects.</p>

        <h2>5. Legal bases (GDPR)</h2>
        <ul>
          <li><strong>Contract</strong> — running your account and delivering the service you paid for.</li>
          <li><strong>Legitimate interest</strong> — security, fraud prevention, aggregate product analytics.</li>
          <li><strong>Consent</strong> — optional email address, opt-in Tester program.</li>
          <li><strong>Legal obligation</strong> — retention of billing records for tax law.</li>
        </ul>

        <h2>6. Data sharing</h2>
        <p>We share personal information only with the processors below, only for the purpose stated, and only under a written data-processing agreement:</p>
        <ul>
          <li><strong>Stripe, Inc.</strong> — card payment processing.</li>
          <li><strong>Alipay (Ant Group)</strong> — Alipay payment processing.</li>
          <li><strong>Google LLC / Meta Platforms, Inc.</strong> — if you use Google or Facebook sign-in, only the sign-in exchange itself.</li>
          <li><strong>Cloudflare, Inc.</strong> — DDoS protection and TLS termination in front of our API. Cloudflare sees only encrypted request metadata.</li>
          <li><strong>Hosting</strong> — the servers powering both our control plane and every VPN node are owned and operated by <strong>TNIDU Inc.</strong> itself. No third-party hosting provider sits in the data path, no third-party administrator has SSH access, and no third party can subpoena the physical hardware without going through TNIDU Inc. as the operator of record. Encrypted disks; keys held by TNIDU Inc. staff only.</li>
        </ul>
        <p>We do not sell, rent, or trade your personal information. We do not share it for cross-context behavioural advertising.</p>

        <h2>7. Data retention</h2>
        <ul>
          <li><strong>Account data</strong> — while your account is active, plus 30 days after you delete it (for reversal in case of account-takeover recovery). After 30 days, hard-deleted from primary storage; encrypted backup rotations flush within 90 days.</li>
          <li><strong>Cumulative byte counter</strong> — reset at each billing cycle; historical totals are not retained.</li>
          <li><strong>Crash / diagnostic logs</strong> — 30 days.</li>
          <li><strong>Billing records</strong> — 7 years, per Sri Lanka tax law.</li>
          <li><strong>Support conversations</strong> — 24 months.</li>
        </ul>

        <h2>8. Your rights</h2>
        <p>You can, at any time and free of charge:</p>
        <ul>
          <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
          <li><strong>Rectify</strong> — correct inaccurate data.</li>
          <li><strong>Delete</strong> — ask us to erase your data ("right to be forgotten"). Use the in-app <em>Delete account</em> flow (Account → Security → Delete account) or email <a href="mailto:privacy@tnidu.com">privacy@tnidu.com</a>.</li>
          <li><strong>Restrict processing</strong> — pause specific processing while a dispute is resolved.</li>
          <li><strong>Port</strong> — receive your data in a machine-readable JSON export.</li>
          <li><strong>Object</strong> — object to processing based on legitimate interest.</li>
          <li><strong>Withdraw consent</strong> — where processing relies on consent, withdraw it at any time (does not affect prior lawful processing).</li>
        </ul>
        <p>We respond within 30 days. If we cannot fulfil a request, we will explain why in the same reply.</p>
        <p><strong>California residents (CCPA / CPRA)</strong>: you have the same rights, plus the right to opt out of sale or sharing. We do not sell or share your personal information — but you can still submit a request to confirm this at any time.</p>

        <h2>9. Data deletion</h2>
        <p>Two paths, both leading to the same result:</p>
        <ul>
          <li><strong>In-app</strong> — Account → Security → <em>Delete account</em>. Scheduled for removal in 7 days; sign in again during that window to cancel. After the window, hard-deleted from primary storage.</li>
          <li><strong>By email</strong> — write to <a href="mailto:privacy@tnidu.com">privacy@tnidu.com</a> from the address on file. We verify identity and complete deletion within 30 days.</li>
        </ul>
        <p>Data we are legally required to keep (e.g. billing records for tax purposes) is retained only for the mandated period, minimised, and never used for any other purpose.</p>

        <h2>10. Security</h2>
        <p>We use industry-standard safeguards:</p>
        <ul>
          <li>All traffic in and out of our services is over TLS 1.3.</li>
          <li>Passwords are bcrypt-hashed with per-user salts. Plaintext is never stored or logged.</li>
          <li>Database volumes are encrypted at rest.</li>
          <li>Admin access is role-scoped, audit-logged, and requires HMAC-signed requests.</li>
          <li>API traffic is rate-limited and monitored for abuse.</li>
        </ul>
        <p>No system is perfectly secure. If we discover a data breach, we will notify affected users and the relevant supervisory authority within 72 hours as required by law.</p>

        <h2>11. Children's privacy</h2>
        <p>Vpnee is not directed at children. We do not knowingly collect personal information from anyone under the age of 13 (or under 16 in the EU / UK, per local law). If you believe a child has provided us with personal data, email <a href="mailto:privacy@tnidu.com">privacy@tnidu.com</a> and we will delete it.</p>

        <h2>12. International data transfers</h2>
        <p>Vpnee operates globally. Where personal data is transferred outside your country, we rely on Standard Contractual Clauses (SCCs) or equivalent safeguards. Both our primary control-plane data (Sri Lanka) and our encrypted backups (EU) are held on hardware owned and operated by TNIDU Inc. — no third-party hosting provider is involved.</p>

        <h2>13. Third-party services</h2>
        <p>Vpnee does not integrate any advertising SDK, third-party analytics SDK, or attribution SDK. The Vpnee mobile app requests only the permissions strictly needed to run a VPN: Internet, Foreground Service, VPN service, Post Notifications (Android 13+). It does not access contacts, SMS, camera, microphone, photos, precise location, calendar, or files.</p>

        <h2>14. Changes to this policy</h2>
        <p>We may update this policy from time to time. Material changes are announced in-app at least 14 days before they take effect. The "Effective" date at the top always shows the current version. Previous versions are archived and available on request.</p>

        <h2>15. How to contact us</h2>
        <ul>
          <li><strong>Privacy questions & rights requests</strong>: <a href="mailto:privacy@tnidu.com">privacy@tnidu.com</a></li>
          <li><strong>General support</strong>: <a href="mailto:hello@tnidu.com">hello@tnidu.com</a></li>
          <li><strong>Billing</strong>: <a href="mailto:billing@tnidu.com">billing@tnidu.com</a></li>
          <li><strong>Abuse / DMCA</strong>: <a href="mailto:abuse@tnidu.com?cc=privacy@tnidu.com">abuse@tnidu.com</a> (also delivered to <a href="mailto:privacy@tnidu.com">privacy@tnidu.com</a>)</li>
          <li><strong>Legal notices</strong>: <a href="mailto:legal@tnidu.com?cc=privacy@tnidu.com">legal@tnidu.com</a> (also delivered to <a href="mailto:privacy@tnidu.com">privacy@tnidu.com</a>)</li>
          <li><strong>Postal</strong>: TNIDU Inc., Colombo 00500, Sri Lanka</li>
        </ul>
        <p>If you are in the EU / UK and are not satisfied with our response, you have the right to complain to your local supervisory authority.</p>

        <div className="meta">
          <span>Effective: {EFFECTIVE}</span>
          <span>TNIDU Inc. — Privacy First</span>
        </div>
      </article>
    </Section>
  );
}
